import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Modal,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import formularioImage from "../../static/media/Contacto/formulario.webp";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  message: "",
};

export default function ContactFormSection() {
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [holdProgress, setHoldProgress] = useState(0);
  const [holdComplete, setHoldComplete] = useState(false);
  const [isHolding, setIsHolding] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorAlert, setErrorAlert] = useState({
    open: false,
    message: "",
  });

  const isFormValid = useMemo(() => {
    return (
      formData.name.trim() &&
      formData.email.trim() &&
      formData.phone.trim() &&
      formData.message.trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())
    );
  }, [formData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Ingresa tu nombre";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Ingresa tu correo";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = "Ingresa un correo válido";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Ingresa tu número";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Escribe tu mensaje";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetHold = () => {
    setIsHolding(false);
    setHoldProgress(0);
    setHoldComplete(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    if (holdComplete) {
      resetHold();
    }
  };

  const startHold = () => {
    if (!isFormValid || isSubmitting) {
      validateForm();
      return;
    }

    setIsHolding(true);
    setHoldProgress(0);
    setHoldComplete(false);

    const startTime = Date.now();
    const duration = 3000;

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / duration) * 100, 100);

      setHoldProgress(progress);

      if (progress >= 100) {
        clearInterval(interval);
        setIsHolding(false);
        setHoldComplete(true);
      }
    }, 30);

    window.__contactHoldInterval = interval;
  };

  const stopHold = () => {
    if (window.__contactHoldInterval) {
      clearInterval(window.__contactHoldInterval);
      window.__contactHoldInterval = null;
    }

    if (!holdComplete) {
      setIsHolding(false);
      setHoldProgress(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const valid = validateForm();

    if (!valid || !holdComplete || isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await fetch("/contact-email/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        if (data?.errors) {
          setErrors(data.errors);
        }

        throw new Error(data?.message || "No se pudo enviar el mensaje");
      }

      setFormData(initialForm);
      setErrors({});
      setSuccessOpen(true);
      resetHold();

      setTimeout(() => {
        setSuccessOpen(false);
      }, 4000);
    } catch (error) {
      setErrorAlert({
        open: true,
        message: error.message || "Ocurrió un error al enviar el mensaje",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Box
        component="section"
        sx={{
          width: "100%",
          py: { xs: 5, md: 8 },
          bgcolor: "#ffffff",
        }}
      >
        <Box
          sx={{
            width: { xs: "90%", md: "85%" },
            mx: "auto",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: { xs: 4, md: 5 },
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                order: { xs: 1, md: 1 },
              }}
            >
              <Box
                component="img"
                src={formularioImage}
                alt="Formulario de contacto NovaXFit"
                sx={{
                  width: "100%",
                  maxWidth: { xs: 430, md: 560 },
                  height: "auto",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>

            <Box
              sx={{
                order: { xs: 2, md: 2 },
              }}
            >
              <Box sx={{ mb: 2.2 }}>
                <Typography
                  component="h2"
                  sx={{
                    color: "#111111",
                    fontWeight: 800,
                    lineHeight: 1.05,
                    letterSpacing: -1,
                    fontSize: {
                      xs: "clamp(30px,6vw,38px)",
                      md: "clamp(40px,3vw,50px)",
                    },
                    mb: 1.4,
                  }}
                >
                  Recibe la información que necesitas
                </Typography>

                <Typography
                  sx={{
                    color: "rgba(17,17,17,0.74)",
                    lineHeight: 1.75,
                    fontSize: { xs: 15.5, md: 16.5 },
                  }}
                >
                  Déjanos tus datos y tu mensaje. Nuestro equipo revisará tu
                  consulta para ayudarte con planes, horarios, servicios o
                  cualquier duda sobre NovaXFit.
                </Typography>
              </Box>

              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  mt: 3,
                }}
              >
                <Stack spacing={2}>
                  <TextField
                    name="name"
                    label="Nombre"
                    value={formData.name}
                    onChange={handleChange}
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name || ""}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "16px",
                        bgcolor: "#ffffff",
                      },
                    }}
                  />

                  <TextField
                    name="email"
                    label="Correo electrónico"
                    value={formData.email}
                    onChange={handleChange}
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email || ""}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "16px",
                        bgcolor: "#ffffff",
                      },
                    }}
                  />

                  <TextField
                    name="phone"
                    label="Número de teléfono"
                    value={formData.phone}
                    onChange={handleChange}
                    fullWidth
                    error={!!errors.phone}
                    helperText={errors.phone || ""}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "16px",
                        bgcolor: "#ffffff",
                      },
                    }}
                  />

                  <TextField
                    name="message"
                    label="Mensaje"
                    value={formData.message}
                    onChange={handleChange}
                    multiline
                    minRows={5}
                    fullWidth
                    error={!!errors.message}
                    helperText={errors.message || ""}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "16px",
                        bgcolor: "#ffffff",
                      },
                    }}
                  />

                  <Box>
                    <Typography
                      sx={{
                        color: "rgba(17,17,17,0.72)",
                        fontSize: 14,
                        lineHeight: 1.6,
                        mb: 1.2,
                      }}
                    >
                      Para habilitar el envío, mantén presionado el botón durante
                      3 segundos.
                    </Typography>

                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: 56,
                        borderRadius: "16px",
                        overflow: "hidden",
                        bgcolor: "rgba(17,17,17,0.06)",
                        border: "1px solid rgba(17,17,17,0.08)",
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          width: `${holdProgress}%`,
                          background:
                            "linear-gradient(135deg,#5f0008 0%,#a80d19 45%,#df2632 100%)",
                          transition: isHolding ? "none" : "width 0.2s ease",
                        }}
                      />

                      <Button
                        type="button"
                        fullWidth
                        onMouseDown={startHold}
                        onMouseUp={stopHold}
                        onMouseLeave={stopHold}
                        onTouchStart={startHold}
                        onTouchEnd={stopHold}
                        disabled={isSubmitting}
                        sx={{
                          position: "relative",
                          zIndex: 1,
                          height: "100%",
                          borderRadius: "16px",
                          textTransform: "none",
                          fontWeight: 800,
                          fontSize: 15,
                          color: holdProgress > 55 || holdComplete ? "#ffffff" : "#111111",
                        }}
                      >
                        {holdComplete
                          ? "Verificación completada"
                          : isHolding
                          ? "Mantén presionado..."
                          : "Mantén 3 segundos para habilitar"}
                      </Button>
                    </Box>
                  </Box>

                  <Button
                    type="submit"
                    disabled={!holdComplete || isSubmitting}
                    sx={{
                      mt: 0.5,
                      height: 54,
                      borderRadius: "16px",
                      textTransform: "none",
                      fontWeight: 800,
                      fontSize: 15,
                      color: "#ffffff",
                      background:
                        "linear-gradient(135deg,#5f0008 0%,#a80d19 40%,#df2632 70%,#161616 100%)",
                      boxShadow: "0 12px 30px rgba(229,9,20,0.22)",
                      "&:hover": {
                        filter: "brightness(1.04)",
                      },
                      "&.Mui-disabled": {
                        color: "rgba(255,255,255,0.7)",
                        background: "rgba(17,17,17,0.2)",
                        boxShadow: "none",
                      },
                    }}
                  >
                    {isSubmitting ? (
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <CircularProgress size={18} sx={{ color: "#ffffff" }} />
                        <Box component="span">Enviando mensaje...</Box>
                      </Stack>
                    ) : (
                      "Enviar mensaje"
                    )}
                  </Button>
                </Stack>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Modal open={successOpen}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "88%", sm: 420 },
            bgcolor: "#ffffff",
            borderRadius: "24px",
            boxShadow: "0 20px 60px rgba(17,17,17,0.18)",
            p: { xs: 3, md: 4 },
            textAlign: "center",
            outline: "none",
          }}
        >
          <Box
            sx={{
              width: 74,
              height: 74,
              borderRadius: "999px",
              mx: "auto",
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(229,9,20,0.08)",
              border: "1px solid rgba(229,9,20,0.14)",
            }}
          >
            <Typography
              sx={{
                color: "#e50914",
                fontSize: 34,
                fontWeight: 800,
                lineHeight: 1,
              }}
            >
              ✓
            </Typography>
          </Box>

          <Typography
            sx={{
              color: "#111111",
              fontWeight: 800,
              fontSize: { xs: 24, md: 28 },
              lineHeight: 1.1,
              mb: 1,
            }}
          >
            Mensaje enviado
          </Typography>

          <Typography
            sx={{
              color: "rgba(17,17,17,0.72)",
              fontSize: 15.5,
              lineHeight: 1.7,
            }}
          >
            Gracias por escribirnos. Hemos recibido tu mensaje correctamente.
          </Typography>
        </Box>
      </Modal>

      <Snackbar
        open={errorAlert.open}
        autoHideDuration={4000}
        onClose={() => setErrorAlert({ open: false, message: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setErrorAlert({ open: false, message: "" })}
          sx={{ width: "100%" }}
        >
          {errorAlert.message}
        </Alert>
      </Snackbar>
    </>
  );
}